import React, { Suspense, lazy, useEffect } from "react";
import { Button, Sidebar, SidePane, ActionBar, Navigator, Alerts, Intros, Billing } from "../../Components";
import { FiMenu, FiBell } from "react-icons/fi";
import { useUserPreference } from "../../Hooks/useUserPreference";
import { Route, Routes, useLocation } from "react-router-dom";
import AccountSettings from "./Common/AccountSettings";
import Notifications from "./Common/Notifications";
import { useUserConfig } from "../../Hooks/useUserConfig";
import Chat from "./Common/Chat";
import Support from "./Common/Support";
import Faqs from "./Common/Faqs";
import VerifyUserDetails from "./Wizards/User/verify";
import useUserProfile from "../../Hooks/useUserProfile";
import { useClientSettings } from "../../Hooks/useClientSettings";
import QuickActions from "./Employee/QuickActions";
import EmployeeRoutes from "./Employee";
import LoadingScreen from "./loading";
import useSocket from "../../Hooks/useSocket";
import AdvisoryMessage from "./advisory";
import { SizeMonitor, ScrollToTop } from "../../Components";
import { useLoginHook } from "Hooks/useLoginHook";
import useMobileCheck from "../../Hooks/useMobileCheck";
const AdminRoutes = lazy(() => import("./Admin"));

function App() {
	useSocket();
	const LOCATION = useLocation();
	const { authenticated } = useLoginHook();
	const { role, userConfig } = useUserConfig();
	const { isAdmin } = userConfig;
	const { isEnabled, advisory } = useClientSettings();
	const { profile } = useUserProfile();
	const { showSidebar, sidebar, sidePane, showSidePane, notificationsOverride, setNotificationsOverride, openNotifications } = useUserPreference();
	const { isMobile } = useMobileCheck();
	const toggleSidebar = () => {
		if (!sidebar) {
			showSidePane(false);
		}
		showSidebar(!sidebar);
	};
	const toggleSidePane = () => {
		if (!sidePane || !notificationsOverride) {
			openNotifications();
			return;
		}
		showSidePane(false);
	};

	useEffect(() => {
		showSidePane(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [LOCATION?.pathname]);

	useEffect(() => {
		const prevBodyOverflow = document.body.style.overflow;
		const prevHtmlOverflow = document.documentElement.style.overflow;
		const mainContainer = document.getElementById("main-container");
		const appScrollHost = document.getElementById("app-scroll-host");
		const prevMainOverflow = mainContainer?.style.overflowY;
		const prevMainTouchAction = mainContainer?.style.touchAction;
		const prevMainPointerEvents = mainContainer?.style.pointerEvents;
		const prevHostOverflow = appScrollHost?.style.overflowY;

		if (isMobile && sidePane) {
			document.body.style.overflow = "hidden";
			document.documentElement.style.overflow = "hidden";
			if (mainContainer) {
				mainContainer.style.overflowY = "hidden";
				mainContainer.style.touchAction = "none";
				mainContainer.style.pointerEvents = "none";
			}
			if (appScrollHost) {
				appScrollHost.style.overflowY = "hidden";
			}
		}

		return () => {
			document.body.style.overflow = prevBodyOverflow;
			document.documentElement.style.overflow = prevHtmlOverflow;
			if (mainContainer) {
				mainContainer.style.overflowY = prevMainOverflow || "";
				mainContainer.style.touchAction = prevMainTouchAction || "";
				mainContainer.style.pointerEvents = prevMainPointerEvents || "";
			}
			if (appScrollHost) {
				appScrollHost.style.overflowY = prevHostOverflow || "";
			}
		};
	}, [isMobile, sidePane]);

	useEffect(() => {
		if (sidebar && sidePane) {
			showSidebar(false);
		}
	}, [sidebar, sidePane, showSidebar]);

	if (userConfig.loading) {
		return <LoadingScreen />;
	}

	if (!profile.gender) {
		return <VerifyUserDetails profile={profile} />;
	}

	if (!authenticated) return null;

	return (
		<>
			<SizeMonitor />
			<ScrollToTop />
			<Intros />
			<Alerts />
			<div className="flex flex-col w-full h-full">
				<ActionBar>
					<Button onClick={toggleSidebar} variant="icon">
						<FiMenu />
					</Button>
					<div className="flex-1 mr-2 flex-center-between">
						<Navigator />
						{/* <CommandPalette /> */}
					</div>
					<Button
						onClick={toggleSidePane}
						variant="icon"
						aria-label="Notifications"
						className={sidePane && notificationsOverride ? "text-primary" : ""}
					>
						<FiBell />
					</Button>
				</ActionBar>
				<div className="flex flex-1 min-h-0 w-full">
					{isMobile && sidebar && <div className="fixed inset-x-0 top-16 bottom-0 z-40 bg-black/20 md:hidden" onClick={() => showSidebar(false)} />}
					<Sidebar>{<AdvisoryMessage advisory={advisory} />}</Sidebar>
					<Billing>
						<div id="app-scroll-host" className="relative flex flex-col flex-1 overflow-x-visible overflow-y-auto min-h-0">
							<div className="main-container scrollbar-autohide flex-1 flex flex-col min-h-0" id="main-container">
								{role === "USER" && <EmployeeRoutes />}
								{role === "ADMIN" && isAdmin && (
									<Suspense fallback={<LoadingScreen />}>
										<AdminRoutes />
									</Suspense>
								)}
								<Routes>
									<Route path="/account/*" element={<AccountSettings />} />
									<Route path="/chat/*" element={<Chat />} />
									<Route path="/support/*" element={<Support />} />
									<Route path="/faqs/*" element={<Faqs />} />

								</Routes>
							</div>
						</div>
					</Billing>
					<SidePane>{isEnabled("ORGANIZATION") ? <Notifications /> : <QuickActions />}</SidePane>
				</div>
			</div>
		</>
	);
}

export default App;
